// src/services/sse/hooks/usePreferenceSSE.ts
/**
 * Hook for preference updates stream.
 */
import { useSSE } from './useSSE.ts';
import {
  PreferenceEvent,
  SSEEventType,
  BaseSSEEvent,
} from '../types.ts';

export interface UsePreferenceSSEReturn {
  overallConfidence: number | null;
  shouldShowOnboarding: boolean | null;
  lowConfidenceSections: string[] | null;
  loading: boolean;
  error: string | null;
  connected: boolean;
  retry?: () => void;
  lastEvent: PreferenceEvent | null;
}

/**
 * Hook for preference updates stream.
 *
 * Listens for:
 * - PREFERENCES_EXTRACTED: When preferences are extracted from resume
 * - PREFERENCES_UPDATED: When user updates preferences
 * - PREFERENCES_SHOW_ONBOARDING: When onboarding modal should be shown
 */
export const usePreferenceSSE = (
  onPreferenceExtracted?: (event: PreferenceEvent) => void,
  onPreferenceUpdated?: (event: PreferenceEvent) => void,
  onShowOnboarding?: (event: PreferenceEvent) => void
): UsePreferenceSSEReturn => {
  console.log('🔌 [PREFERENCE SSE HOOK] Initializing preference SSE connection...');

  const { data, loading, error, connected, retry } = useSSE<PreferenceEvent>(
    'preferences',
    {
      onMessage: (eventData: BaseSSEEvent) => {
        console.log('🔥 [PREFERENCE SSE HOOK] RAW event received:', eventData);
        const event = eventData as PreferenceEvent;
        console.log('🔥 [PREFERENCE SSE HOOK] Parsed event:', event);
        console.log('🔥 [PREFERENCE SSE HOOK] Event type:', event.type);

        if (event.type === SSEEventType.PREFERENCES_EXTRACTED) {
          console.log('✨ [PREFERENCE SSE HOOK] Preferences extracted from resume:', {
            confidence: event.overall_confidence,
            showOnboarding: event.should_show_onboarding,
            lowConfidenceSections: event.low_confidence_sections,
          });
          onPreferenceExtracted?.(event);
        } else if (event.type === SSEEventType.PREFERENCES_UPDATED) {
          console.log('📝 [PREFERENCE SSE HOOK] Preferences updated:', {
            confidence: event.overall_confidence,
          });
          onPreferenceUpdated?.(event);
        } else if (event.type === SSEEventType.PREFERENCES_SHOW_ONBOARDING) {
          console.log('🎯 [PREFERENCE SSE HOOK] Show onboarding modal:', {
            lowConfidenceSections: event.low_confidence_sections,
          });
          onShowOnboarding?.(event);
        }
      },
      onConnect: () => {
        console.log('✅ [PREFERENCE SSE HOOK] Connected to preference SSE stream!');
      },
      onError: (err) => {
        console.error('❌ [PREFERENCE SSE HOOK] SSE connection error:', err);
      },
      debug: true,
    }
  );

  console.log('🔌 [PREFERENCE SSE HOOK] Connection state:', {
    loading,
    error,
    connected,
    hasData: !!data,
  });

  return {
    overallConfidence: data?.overall_confidence ?? null,
    shouldShowOnboarding: data?.should_show_onboarding ?? null,
    lowConfidenceSections: data?.low_confidence_sections ?? null,
    loading,
    error,
    connected,
    retry,
    lastEvent: data,
  };
};
